import { Badge, Button, Card, CardBody, Input, Modal, Select, Spinner } from '@foundation/ui'
import { createFileRoute, redirect } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import styles from './ab-tests.module.css'
import { AppLayout } from '@/components/AppLayout'
import { api, type ABTest, type Sequence } from '@/lib/api'
import { isAuthenticated } from '@/lib/auth-store'
import { toast } from '@/lib/toast'

export const Route = createFileRoute('/ab-tests')({
  beforeLoad: () => {
    if (!isAuthenticated()) {
      throw redirect({ to: '/login' })
    }
  },
  component: ABTestsPage,
})

function getStatusBadge(status: string) {
  switch (status) {
    case 'running':
      return <Badge variant="primary">Running</Badge>
    case 'completed':
      return <Badge variant="success">Completed</Badge>
    case 'draft':
      return <Badge variant="default">Draft</Badge>
    default:
      return <Badge variant="default">{status}</Badge>
  }
}

function ABTestsPage() {
  const [tests, setTests] = useState<ABTest[]>([])
  const [sequences, setSequences] = useState<Sequence[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [showCreateModal, setShowCreateModal] = useState(false)

  // Create form state
  const [selectedSequenceId, setSelectedSequenceId] = useState('')
  const [selectedStepNumber, setSelectedStepNumber] = useState('1')
  const [variantBSubject, setVariantBSubject] = useState('')
  const [trafficSplit, setTrafficSplit] = useState('50')
  const [metric, setMetric] = useState('open_rate')
  const [isCreating, setIsCreating] = useState(false)

  useEffect(() => {
    Promise.all([
      api.listABTests().catch(() => ({ data: [] as ABTest[] })),
      api.listSequences().catch(() => ({ data: [] as Sequence[] })),
    ])
      .then(([testsRes, sequencesRes]) => {
        setTests(testsRes.data)
        setSequences(sequencesRes.data)
        const firstSequence = sequencesRes.data[0]
        if (firstSequence) {
          setSelectedSequenceId(firstSequence.id)
        }
      })
      .catch((err: unknown) => {
        toast.error(err instanceof Error ? err.message : 'Failed to load A/B tests')
      })
      .finally(() => setIsLoading(false))
  }, [])

  const handleCreate = () => {
    if (!selectedSequenceId) {
      toast.error('Please select a sequence')
      return
    }

    setIsCreating(true)
    api
      .createABTest(selectedSequenceId, parseInt(selectedStepNumber, 10), {
        variant_b: { subject_template: variantBSubject },
        traffic_split: parseInt(trafficSplit, 10),
        metric,
      })
      .then((res) => {
        setTests((prev) => [...prev, res.data])
        setShowCreateModal(false)
        setVariantBSubject('')
        toast.success('A/B test created')
      })
      .catch((err: unknown) => {
        toast.error(err instanceof Error ? err.message : 'Failed to create A/B test')
      })
      .finally(() => setIsCreating(false))
  }

  const handleDeclareWinner = (testId: string, winner: 'a' | 'b') => {
    api
      .declareWinner(testId, winner)
      .then((res) => {
        setTests((prev) => prev.map((t) => (t.id === testId ? res.data : t)))
        toast.success(`Variant ${winner.toUpperCase()} declared as winner`)
      })
      .catch((err: unknown) => {
        toast.error(err instanceof Error ? err.message : 'Failed to declare winner')
      })
  }

  const handleDelete = (testId: string) => {
    if (!window.confirm('Are you sure you want to delete this A/B test?')) return

    api
      .deleteABTest(testId)
      .then(() => {
        setTests((prev) => prev.filter((t) => t.id !== testId))
        toast.success('A/B test deleted')
      })
      .catch((err: unknown) => {
        toast.error(err instanceof Error ? err.message : 'Failed to delete A/B test')
      })
  }

  const getSequenceName = (sequenceId: string): string => {
    return sequences.find((s) => s.id === sequenceId)?.name ?? 'Unknown Sequence'
  }

  const selectedSequence = sequences.find((s) => s.id === selectedSequenceId)
  const stepOptions = selectedSequence
    ? selectedSequence.steps.map((s) => ({
        value: s.stepNumber.toString(),
        label: `Step ${s.stepNumber}: ${s.subjectTemplate || '(No subject)'}`,
      }))
    : [{ value: '1', label: 'Step 1' }]

  if (isLoading) {
    return (
      <AppLayout title="A/B Tests">
        <div style={{ display: 'flex', justifyContent: 'center', padding: '3rem' }}>
          <Spinner size="lg" />
        </div>
      </AppLayout>
    )
  }

  return (
    <AppLayout title="A/B Tests">
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <span className={styles.headerTitle}>
            {tests.length} test{tests.length !== 1 ? 's' : ''}
          </span>
          <span className={styles.proBadge}>Pro+</span>
        </div>
        <Button variant="primary" onClick={() => setShowCreateModal(true)}>
          Create A/B Test
        </Button>
      </div>

      {/* Tests List */}
      {tests.length === 0 ? (
        <Card className={styles.emptyState}>
          <CardBody>
            <div className={styles.emptyIcon}>🧪</div>
            <h3 className={styles.emptyTitle}>No A/B tests yet</h3>
            <p className={styles.emptyDescription}>
              A/B testing helps you optimize your win-back emails by comparing different versions.
              Test subject lines, email body content, CTAs, and discount offers to find what works
              best for recovering cancelled subscribers.
            </p>
            <Button variant="primary" onClick={() => setShowCreateModal(true)}>
              Create Your First A/B Test
            </Button>
          </CardBody>
        </Card>
      ) : (
        <div className={styles.testsGrid}>
          {tests.map((test) => {
            const variantB = test.variantB as Record<string, string>
            const hasWinner = test.winner !== null

            return (
              <Card key={test.id} className={styles.testCard}>
                <CardBody>
                  <div className={styles.testHeader}>
                    <div>
                      <span className={styles.testName}>
                        {getSequenceName(test.sequenceId)} - Step {test.stepNumber}
                      </span>
                    </div>
                    {getStatusBadge(test.status)}
                  </div>

                  <div className={styles.testMeta}>
                    Metric: {test.metric.replace('_', ' ')} | Traffic Split: {test.trafficSplit}% /{' '}
                    {100 - test.trafficSplit}%
                  </div>

                  {/* Variants */}
                  <div className={styles.variants}>
                    <div
                      className={`${styles.variant} ${
                        test.winner === 'a' ? styles.variantWinner : ''
                      }`}
                    >
                      <div className={styles.variantLabel}>Variant A (Control)</div>
                      <div className={styles.variantTitle}>Original</div>
                      {test.winner === 'a' && <span className={styles.winnerIcon}>Winner</span>}
                    </div>

                    <span className={styles.vsLabel}>VS</span>

                    <div
                      className={`${styles.variant} ${
                        test.winner === 'b' ? styles.variantWinner : ''
                      }`}
                    >
                      <div className={styles.variantLabel}>Variant B</div>
                      <div className={styles.variantTitle}>
                        {variantB.subject_template ?? 'Modified'}
                      </div>
                      {test.winner === 'b' && <span className={styles.winnerIcon}>Winner</span>}
                    </div>
                  </div>

                  {/* Footer */}
                  <div className={styles.testFooter}>
                    <span className={styles.testDate}>
                      Created {new Date(test.createdAt).toLocaleDateString()}
                    </span>
                    <div className={styles.testActions}>
                      {test.status === 'running' && !hasWinner && (
                        <>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeclareWinner(test.id, 'a')}
                          >
                            A Wins
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeclareWinner(test.id, 'b')}
                          >
                            B Wins
                          </Button>
                        </>
                      )}
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(test.id)}>
                        Delete
                      </Button>
                    </div>
                  </div>
                </CardBody>
              </Card>
            )
          })}
        </div>
      )}

      {/* Create Modal */}
      <Modal
        open={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        title="Create A/B Test"
        footer={
          <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
            <Button variant="secondary" onClick={() => setShowCreateModal(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleCreate}
              loading={isCreating}
              disabled={!selectedSequenceId || !variantBSubject.trim()}
            >
              Create Test
            </Button>
          </div>
        }
      >
        <div className={styles.createForm}>
          <Select
            label="Sequence"
            value={selectedSequenceId}
            onChange={(e) => {
              setSelectedSequenceId(e.target.value)
              setSelectedStepNumber('1')
            }}
            options={sequences.map((s) => ({ value: s.id, label: s.name }))}
          />

          <Select
            label="Email Step"
            value={selectedStepNumber}
            onChange={(e) => setSelectedStepNumber(e.target.value)}
            options={stepOptions}
          />

          <Input
            label="Variant B Subject Line"
            type="text"
            value={variantBSubject}
            onChange={(e) => setVariantBSubject(e.target.value)}
            placeholder="Enter the alternative subject line to test..."
            required
          />

          <Select
            label="Success Metric"
            value={metric}
            onChange={(e) => setMetric(e.target.value)}
            options={[
              { value: 'open_rate', label: 'Open Rate' },
              { value: 'click_rate', label: 'Click Rate' },
              { value: 'conversion_rate', label: 'Conversion Rate' },
            ]}
          />

          <Input
            label="Traffic Split (% to Variant A)"
            type="number"
            value={trafficSplit}
            onChange={(e) => setTrafficSplit(e.target.value)}
            min="10"
            max="90"
          />
        </div>
      </Modal>
    </AppLayout>
  )
}
